import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNotify } from '@/hooks/useNotify';
import { Trash2, Plus, Edit, Save, X, LockIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import CategoryMarkupTiers from './CategoryMarkupTiers';
import { CategoryWithMarkupTiers } from '@/types/category-markup-tiers';

const CategoriesSettings = () => {
  const { user, profile } = useAuth();
  const notify = useNotify();
  const [categories, setCategories] = useState<CategoryWithMarkupTiers[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [editingCategory, setEditingCategory] = useState<CategoryWithMarkupTiers | null>(null);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.company_id) {
      fetchCategories();
    } else {
      setLoading(false);
    }
  }, [profile]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_all_categories', { user_company_id: profile?.company_id })
        .order('is_default', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;
      
      const categoriesWithTiers = data?.map(category => ({
        ...category,
        markup_tiers: []
      })) || [];
      
      setCategories(categoriesWithTiers);
    } catch (error) {
      console.error('Error fetching categories:', error);
      notify.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    if (expandedCategoryId === categoryId) {
      setExpandedCategoryId(null);
    } else {
      setExpandedCategoryId(categoryId);
    }
  };

  const addCategory = async () => {
    try {
      if (!newCategory.name.trim()) {
        notify.warning("Required Field", "Category name is required.");
        return;
      }

      const companyId = profile?.company_id;
      
      if (!companyId) {
        notify.warning("Authentication Error", "You must be logged in with a company account to add categories.");
        return;
      }

      const { data, error } = await supabase
        .from('supplier_categories')
        .insert({
          name: newCategory.name.trim(),
          description: newCategory.description || null,
          company_id: companyId,
          is_default: false
        })
        .select();

      if (error) {
        console.error("Insert error:", error);
        throw error;
      }

      if (data) {
        const newCategoryWithTiers: CategoryWithMarkupTiers = {
          ...data[0],
          markup_tiers: []
        };
        
        setCategories([...categories, newCategoryWithTiers]);
        setNewCategory({ name: '', description: '' });
        notify.success("Category added successfully");
      }
    } catch (error: any) {
      console.error('Error adding category:', error);
      notify.error(error);
    }
  };

  const startEdit = (category: CategoryWithMarkupTiers) => {
    if (category.is_default) {
      notify.warning("Cannot edit default category", "Default categories cannot be modified.");
      return;
    }
    setEditingCategory({ ...category });
  };

  const cancelEdit = () => {
    setEditingCategory(null);
  };

  const saveEdit = async () => {
    try {
      if (!editingCategory || !editingCategory.name.trim()) return;

      const { data, error } = await supabase
        .from('supplier_categories')
        .update({
          name: editingCategory.name.trim(),
          description: editingCategory.description
        })
        .eq('id', editingCategory.id)
        .select();

      if (error) throw error;

      if (data) {
        const updatedCategory: CategoryWithMarkupTiers = {
          ...data[0],
          markup_tiers: editingCategory.markup_tiers
        };
        
        setCategories(categories.map(c => c.id === editingCategory.id ? updatedCategory : c));
        setEditingCategory(null);
        notify.success("Category updated successfully");
      }
    } catch (error) {
      console.error('Error updating category:', error);
      notify.error(error);
    }
  };

  const deleteCategory = async (id: string, isDefault: boolean) => {
    if (isDefault) {
      notify.warning("Cannot delete default category", "Default categories cannot be removed.");
      return;
    }
    
    try {
      const { error } = await supabase
        .from('supplier_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setCategories(categories.filter(c => c.id !== id));
      notify.success("Category deleted successfully");
    } catch (error) {
      console.error('Error deleting category:', error);
      notify.error(error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Line Item Categories</CardTitle>
        <CardDescription>Manage categories for your line items</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Add new category form */}
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-12 sm:gap-4 mb-6">
            <div className="sm:col-span-5">
              <Input 
                placeholder="Category name" 
                value={newCategory.name}
                onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
              />
            </div>
            <div className="sm:col-span-5">
              <Input 
                placeholder="Description (optional)" 
                value={newCategory.description}
                onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
              />
            </div>
            <div className="sm:col-span-2">
              <Button 
                onClick={addCategory} 
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          {/* Categories list */}
          <div className="space-y-4">
            {loading ? (
              <p className="text-center py-4 text-muted-foreground">Loading categories...</p>
            ) : categories.length === 0 ? (
              <p className="text-center py-4 text-muted-foreground">No categories found. Add your first category above.</p>
            ) : (
              <>
                {categories.map((category) => (
                  <div key={category.id} className="border rounded-md overflow-hidden">
                    <div className="flex flex-col gap-3 p-3 sm:grid sm:grid-cols-12 sm:gap-4 sm:items-center">
                      {editingCategory && editingCategory.id === category.id ? (
                        <>
                          <div className="sm:col-span-5">
                            <Input 
                              value={editingCategory.name}
                              onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                            />
                          </div>
                          <div className="sm:col-span-5">
                            <Input 
                              value={editingCategory.description || ''}
                              onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                              placeholder="Description (optional)"
                            />
                          </div>
                          <div className="sm:col-span-2 flex gap-2 justify-end">
                            <Button size="icon" variant="outline" onClick={cancelEdit}>
                              <X className="h-4 w-4" />
                            </Button>
                            <Button size="icon" onClick={saveEdit}>
                              <Save className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="sm:col-span-5 font-medium flex items-center gap-2">
                            {category.name}
                            {category.is_default && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <LockIcon className="h-3 w-3" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <div className="sm:col-span-5 text-muted-foreground">
                            {category.description || <em>No description</em>}
                          </div>
                          <div className="sm:col-span-2 flex gap-2 justify-end">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => toggleCategoryExpansion(category.id)}
                            >
                              {expandedCategoryId === category.id ? 
                                <ChevronUp className="h-4 w-4" /> : 
                                <ChevronDown className="h-4 w-4" />
                              }
                            </Button>
                            <Button 
                              size="icon" 
                              variant="outline" 
                              onClick={() => startEdit(category)}
                              disabled={category.is_default}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="outline" 
                              onClick={() => deleteCategory(category.id, category.is_default)}
                              disabled={category.is_default}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                    {expandedCategoryId === category.id && (
                      <div className="border-t px-3 pb-3">
                        <CategoryMarkupTiers 
                          category={category}
                          onUpdate={fetchCategories}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoriesSettings;
